import React, { useState } from 'react';
import styles from './_rule-override-editor.module.scss';
import { Button } from '@progress/kendo-react-buttons';
import { GenericGrid, hasValue } from '@amsanalytics/react-shared-library';
import { TextArea } from '@progress/kendo-react-inputs';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { BusinessRuleOverride, BusinessRuleOverrideStatus } from '../../utils/BusinessRulesEngine';
import { GridCellProps } from '@progress/kendo-react-grid';
import { businessRuleOverrideStatuses, businessRuleOverrideStatusTypes } from '../../utils/constants';
import { isNotNil } from 'ramda';

export interface RuleOverrideEditorProps {
  canOverride: boolean; // whether the user can override the status of an item
  businessRuleOverrideItems: BusinessRuleOverride[]; // list of items to show
  businessRuleOverrideStatuses: BusinessRuleOverrideStatus[]; // list of statuses to choose from
  onOverrideItem: (businessRuleOverrideId: string, overrideStatusId: string, comments: string) => void;
  onUnwaiveItem: (businessRuleOverrideId: string) => void;
}

export default function RuleOverrideEditor(props: RuleOverrideEditorProps) {
  const [isOverrideWindowOpen, setIsOverrideWindowOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [comments, setComments] = useState('');
  const canOverride = props.canOverride;
  const onUnwaiveItem = props.onUnwaiveItem;

  // User has overriden the status of an item.
  const handleOverride = () => {
    if (selectedItemId) {
      props.onOverrideItem(
        selectedItemId,
        businessRuleOverrideStatusTypes.waived,
        comments
      );
      closeWindowAndResetState();
    }
  };

  // Closes the window and resets the state variables.
  const closeWindowAndResetState = () => {
    setIsOverrideWindowOpen(false);
    setSelectedItemId(null);
    setComments("");
  };

  const ApplicationFieldCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <span className={styles.applicationFieldDisplayName}>{hasValue(dataItem.applicationFieldDisplayName) ? dataItem.applicationFieldDisplayName : 'N/A'}</span>
      </td>
    );
  }

  const ActualValueCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <span className={styles.actualValue}>{isNotNil(dataItem.actualValue) ? dataItem.actualValue : 'N/A'}</span>
      </td>
    );
  }

  const ThresholdValueCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <span className={styles.thresholdValue}>{hasValue(dataItem.thresholdValue) ? dataItem.thresholdValue : 'N/A'}</span>
      </td>
    );
  }

  const StatusCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <span className={isNotNil(businessRuleOverrideStatuses.find(status => status.id.toLowerCase() === dataItem.businessRuleOverrideStatusId?.toLowerCase())) ? `${styles.status} ${styles[businessRuleOverrideStatuses.find(status => status.id.toLowerCase() === dataItem.businessRuleOverrideStatusId?.toLowerCase())!.name.toLowerCase()]}` : styles.status}>
          {businessRuleOverrideStatuses.find(status => status.id.toLowerCase() === dataItem.businessRuleOverrideStatusId?.toLowerCase())?.name ?? 'N/A'}
        </span>
      </td>
    );
  };

  // Only show the "Waived By" name if the item has been waived.
  const WaivedByCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        {dataItem.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase() && (
          <span className={styles.applicationFieldDisplayName}>{dataItem.modifiedBy}</span>
        )}
      </td>
    );
  }

  // Only show the "Waived Time" if the item has been waived. Format the date to show only the date.
  const WaivedTimeCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        {dataItem.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase() && (
          <span className={styles.applicationFieldDisplayName}>{new Date(dataItem.modifiedDate).toLocaleDateString()}</span>
        )}
      </td>
    );
  }

  // Users cannot waive passed items.
  const ActionCell = (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        {canOverride && dataItem.businessRuleOverrideStatusId?.toUpperCase() !== businessRuleOverrideStatusTypes.pass.toUpperCase() && (
          <Button
            onClick={() => {
              if (dataItem.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase()) {
                onUnwaiveItem(dataItem.id);
              }
              else {
                setSelectedItemId(dataItem.id);
                setIsOverrideWindowOpen(true);
              }
            }}
            themeColor={'base'}
            fillMode={'outline'}
            size={'small'}
          >
            {dataItem.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase() ? 'Unwaive' : 'Waive'}
          </Button>
        )}
      </td>
    );
  };

  return (
    <div className={styles.ruleOverrideEditorContainer}>
      <GenericGrid<BusinessRuleOverride>
        columns={[
          { field: 'applicationFieldDisplayName', title: 'Field', cell: ApplicationFieldCell, preventSorting: false },
          { field: 'businessRuleOverrideStatusId', title: 'Status', cell: StatusCell, width: '150px', preventSorting: false },
          { field: 'thresholdValue', title: 'Threshold', cell: ThresholdValueCell, preventSorting: false },
          { field: 'actualValue', title: 'Actual', cell: ActualValueCell, preventSorting: false },
          { field: 'reason', title: 'Comments', preventSorting: false },
          { field: 'modifiedBy', title: 'Waived By', cell: WaivedByCell, preventSorting: false },
          { field: 'modifiedDate', title: 'Waived Time', cell: WaivedTimeCell, preventSorting: false },
          { field: 'actions', title: 'Actions', cell: ActionCell, width: '150px', preventFiltering: true, preventSorting: true }
        ]}
        sortDescriptors={[{ field: 'sortOrder', dir: 'asc' }]}
        disableGrouping={true}
        disableAdding={true}
        disableEditing={true}
        items={props.businessRuleOverrideItems}
      />

      {isOverrideWindowOpen && (
        <Window
          title={props.businessRuleOverrideItems.find(item => item.id === selectedItemId)?.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase() ? 'Unwaive Item' : 'Waive Item'}
          onClose={closeWindowAndResetState}
          width={400}
          height={270}
          themeColor='primary'
        >
          <div className={styles.dialogContent}>
            <p style={{ margin: 0 }}>You're about to override the status of this item and change its status to <em>waived</em>.</p>
            <div className={styles.formGroup}>
              <label>Comments</label>
              <TextArea
                value={comments}
                onChange={(e) => setComments(e.value)}
                className={styles.textarea}
                placeholder="Why are you waiving this item?"
              />
            </div>
            <WindowActionsBar>
              <div className={styles.dialogActions}>
                <Button onClick={closeWindowAndResetState} fillMode={'outline'}>
                  Cancel
                </Button>
                <Button
                  onClick={handleOverride}
                  disabled={!hasValue(comments)}
                  themeColor={'success'}
                >
                  {props.businessRuleOverrideItems.find(item => item.id === selectedItemId)?.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived.toLowerCase() ? 'Unwaive' : 'Waive'}
                </Button>
              </div>
            </WindowActionsBar>
          </div>
        </Window>
      )}
    </div>
  );
}







